import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import screenfull from 'screenfull'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportOohPreview } from 'src/supportPortal/services/oohManagementApi'
import OneMentorVideoAndOther from './components/layouts/OneMentorVideoAndOther'

/**
 * Full-screen, support-scoped live preview of an OOH/token screen. Rendered at
 * the top-level route /ooh-preview/:clinicLocationId/:mentorId and fed by the
 * support preview bundle (no kiosk role required). Reuses the exact
 * presentational layout so it renders "as is".
 */
export default function SupportOohPreview() {
  const { clinicLocationId, mentorId } = useParams()
  const [loading, setLoading] = useState(true)
  const [bundle, setBundle] = useState(null)
  const [error, setError] = useState('')
  const [timeNow, setTimeNow] = useState(dayjs())

  useEffect(() => {
    const id = setInterval(() => setTimeNow(dayjs()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const resp = await getSupportOohPreview(clinicLocationId, mentorId)
        if (cancelled) return
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          setBundle(getDataMapFromCommonResponse(resp))
        } else {
          setError('Could not load preview (not authorized or screen not found).')
        }
      } catch (e) {
        console.error('ooh preview load failed', e)
        if (!cancelled) setError('Could not load preview.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (clinicLocationId && mentorId) load()
    return () => {
      cancelled = true
    }
  }, [clinicLocationId, mentorId])

  // Re-fetch the live token queue periodically so the preview mirrors the screen.
  useEffect(() => {
    if (!clinicLocationId || !mentorId) return
    const id = setInterval(async () => {
      try {
        const resp = await getSupportOohPreview(clinicLocationId, mentorId)
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          setBundle(getDataMapFromCommonResponse(resp))
        }
      } catch {
        /* transient; keep last good bundle */
      }
    }, 30000)
    return () => clearInterval(id)
  }, [clinicLocationId, mentorId])

  if (loading) return <LoadingSpinner bgColor="#0f1419" dark />
  if (error || !bundle) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f1419',
          color: '#fff',
          fontSize: '18px',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        {error || 'No preview available.'}
      </div>
    )
  }

  const clinic = bundle.clinic || {}
  const brand = bundle.brand || {}
  const mentor = bundle.mentor || {}
  const mentorProfileData = {
    id: mentor.id,
    name: mentor.name,
    firstName: mentor.name,
    slug: mentor.slug,
    profilePicId: mentor.profilePicId,
  }

  return (
    <OneMentorVideoAndOther
      thisClinicData={{ id: clinic.id, title: clinic.title, name: clinic.title, slug: clinic.slug }}
      clinicBrandData={{ clinicBrandName: brand.name, name: brand.name }}
      mentorProfileData={mentorProfileData}
      primaryMentorCheckedIn={true}
      OOHData={Array.isArray(bundle.oohMessages) ? bundle.oohMessages : []}
      bookedSlotsInDay={Array.isArray(bundle.bookedSlots) ? bundle.bookedSlots : []}
      bookedSlotsInDayLoading={false}
      screeningMentorDataLoading={false}
      screeningMentors={[]}
      handleRefreshBookedSlots={() => {}}
      handleExitFullScreen={() => screenfull.isEnabled && screenfull.exit()}
      handleClickFullScreen={() => {
        if (screenfull.isEnabled) screenfull.request(document.getElementById('fullScreenTarget'))
      }}
      timeNow={timeNow}
      screenfull={screenfull}
      oohConfig={bundle.oohConfig || {}}
      walkinQr={bundle.walkinQrUrl ? { url: bundle.walkinQrUrl } : null}
    />
  )
}
